import {
  DEFAULT_ONBOARDING_VARIANT_ID,
  getOnboardingFlowEntryPointType,
  OnboardingConcernOptionEnum,
  OnboardingEntryPointType,
  OnboardingStepEnum,
  OnboardingVariantIdType,
} from "@/domain/onboarding";
import type { I18nKeyType } from "@/i18n";

type OnboardingI18nPathOfType<K> = K extends `native:onboarding.${infer P}`
  ? P extends `variants.${string}`
    ? never
    : P
  : never;

export type OnboardingI18nPathType = OnboardingI18nPathOfType<I18nKeyType>;

// A piece of onboarding copy, ready to spread onto VnText.
export type OnboardingTextType = {
  i18nKey: I18nKeyType | I18nKeyType[];
  i18nOptions?: object;
};

/**
 * Defines which onboarding steps should include the awareness campaign
 * suffix in the i18n context, keyed by entry point type.
 */
const AWARENESS_RELEVANT_STEPS: Partial<
  Record<OnboardingEntryPointType, OnboardingStepEnum[]>
> = {
  AGING_INTO_MEDICARE: [
    OnboardingStepEnum.INTRO,
    OnboardingStepEnum.BENEFITS,
    OnboardingStepEnum.NEXT_STEPS,
  ],
  REDETERMINATION: [OnboardingStepEnum.NEXT_STEPS],
  RECURRING_WORK_REQUIREMENT: [OnboardingStepEnum.NEXT_STEPS],
};

export const checkIfAwarenessRelevantForStep = (
  entryPointType: OnboardingEntryPointType | undefined,
  step: OnboardingStepEnum,
): boolean => {
  if (!entryPointType) return false;
  return AWARENESS_RELEVANT_STEPS[entryPointType]?.includes(step) ?? false;
};

export const buildOnboardingI18nKeyContext = ({
  entryPointType,
  isAwarenessCampaign,
  isAwarenessRelevantForStep,
  isMedicaidActive,
  isMedicareActive,
}: {
  entryPointType: OnboardingEntryPointType | undefined;
  isAwarenessCampaign: boolean;
  isAwarenessRelevantForStep: boolean;
  isMedicaidActive: boolean;
  isMedicareActive: boolean;
}) => {
  let context: string = entryPointType
    ? getOnboardingFlowEntryPointType(entryPointType)
    : "";

  if (isAwarenessCampaign && isAwarenessRelevantForStep) {
    context += "_awareness";
  }

  if (entryPointType === "SERVICE_INTRODUCTION") {
    // Medicaid takes precedence when both are active.
    if (isMedicaidActive) {
      context += "_medicaid";
    } else if (isMedicareActive) {
      context += "_medicare";
    }
  }

  return context;
};

// A non-default variant's key comes first so its overrides win; i18next falls
// back to the default onboarding key for every context/plural suffix the
// variant doesn't override.
export const buildOnboardingI18nKey = ({
  variantId,
  path,
}: {
  variantId: OnboardingVariantIdType;
  path: OnboardingI18nPathType;
}): I18nKeyType | I18nKeyType[] => {
  const defaultKey: I18nKeyType = `native:onboarding.${path}`;

  if (variantId === DEFAULT_ONBOARDING_VARIANT_ID) {
    return defaultKey;
  }

  // Overrides usually exist only with a context suffix (e.g. `title_LAPSE`),
  // so the unsuffixed key isn't part of the typed union.
  const variantKey =
    `native:onboarding.variants.${variantId}.${path}` as I18nKeyType;

  return [variantKey, defaultKey];
};

export const getConcernOptionSubtextI18nPath = (
  option: OnboardingConcernOptionEnum | null,
): OnboardingI18nPathType => {
  switch (option) {
    case OnboardingConcernOptionEnum.PAYING_FOR_DOCTOR:
      return "payingForDoctor.subtext";
    case OnboardingConcernOptionEnum.NOT_KNOWING_IM_COVERED:
      return "notKnowingImCovered.subtext";
    case OnboardingConcernOptionEnum.NOT_KNOWING_HOW_TO_GET_COVERED:
      return "notKnowingHowToGetCovered.subtext";
    case OnboardingConcernOptionEnum.PAPERWORK:
      return "paperwork.subtext";
    case OnboardingConcernOptionEnum.MISSING_ENROLLMENT_DEADLINE:
      return "missingEnrolmentDeadline.subtext";
    case OnboardingConcernOptionEnum.TOO_MANY_MEDICARE_ADS_AND_CALLS:
      return "tooManyMedicareAdsAndCalls.subtext";
    case OnboardingConcernOptionEnum.KEEPING_MY_CURRENT_DOCTORS:
      return "keepingMyCurrentDoctors.subtext";
    case OnboardingConcernOptionEnum.SUBMITTING_RENEWALS:
      return "submittingRenewals.subtext";
    case OnboardingConcernOptionEnum.FIGURING_OUT_WHEN_TO_RENEW:
      return "figuringOutWhenToRenew.subtext";
    case OnboardingConcernOptionEnum.PART_A_AND_B:
      return "partAB.subtext";
    case OnboardingConcernOptionEnum.PART_A_B_AND_D:
      return "partABD.subtext";
    case OnboardingConcernOptionEnum.PART_C:
      return "partC.subtext";
    case OnboardingConcernOptionEnum.IM_NOT_SURE:
      return "imNotSure.subtext";
    case OnboardingConcernOptionEnum.MEETING_REQUIREMENTS:
      return "meetingRequirements.subtext";
    case OnboardingConcernOptionEnum.FIGURING_OUT_WHEN_TO_SUBMIT_REQUIREMENTS:
      return "figuringOutWhenToSubmitRequirements.subtext";
    default:
      return "somethingElse.subtext";
  }
};
